import { Dialog, Transition, TransitionChild } from "@headlessui/react";
import confetti from "canvas-confetti";
import { useEffect, useRef } from "react";
import { Fragment } from "react";
import RMLogo from "@/assets/images/rm-logo-emblem.svg";
import { Button, Typography } from "@/components/atoms";

interface WelcomeOverlayProps {
  open: boolean;
  userType: "operator" | "client" | "invite";
  onClose: () => void;
}

export const WelcomeOverlay = ({ open, userType, onClose }: WelcomeOverlayProps) => {
  const confettiTriggered = useRef(false);

  useEffect(() => {
    if (open && !confettiTriggered.current) {
      confettiTriggered.current = true;

      confetti({
        particleCount: 100,
        spread: 70,
        origin: { x: 0, y: 0.6 },
        angle: 45,
      });

      confetti({
        particleCount: 100,
        spread: 70,
        origin: { x: 1, y: 0.6 },
        angle: 135,
      });
    }
  }, [open]);

  const getContent = () => {
    switch (userType) {
      case "operator":
        return {
          title: "Welcome to RideMinder! 🎉",
          description: "We're excited to have you here. Let's walk through a few quick steps to get your account and business ready to go.",
          buttonText: "Get Started",
        };
      case "client":
        return {
          title: "Welcome to RideMinder! 🎉",
          description: "We're glad to have you here. Thanks for being part of RideMinder.",
          buttonText: "Get Started",
        };
      case "invite":
        return {
          title: "Welcome to RideMinder! 🎉",
          description: "We're glad to have you here. Thanks for being part of RideMinder.",
          buttonText: "Get Started",
        };
      default:
        return {
          title: "Welcome to RideMinder! 🎉",
          description: "We're excited to have you here. Let's walk through a few quick steps to get your account and business ready to go.",
          buttonText: "Get Started",
        };
    }
  };

  const content = getContent();

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[rgba(29,41,57,0.90)] transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-[100] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="relative w-full max-w-2xl transform overflow-visible text-center transition-all">
                {/* RideMinder Logo */}
                <div className="mb-8 flex justify-center">
                  <div className="flex h-[99px] w-[99px] items-center justify-center rounded-2xl bg-white p-4">
                    <img src={RMLogo} alt="RideMinder" className="h-full w-full" />
                  </div>
                </div>

                {/* Content */}
                <div className="mb-8 space-y-4">
                  <Typography variant="h2" className="text-white">
                    {content.title}
                  </Typography>
                  <Typography variant="paragraph" className="mx-auto max-w-xl text-white">
                    {content.description}
                  </Typography>
                </div>

                {/* Button */}
                <Button variant="primary" size="lg" onClick={onClose} className="w-full min-w-[200px] md:w-auto">
                  {content.buttonText}
                </Button>
              </div>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
